import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  Close,
  PhoneAndroid,
  AccountBalance,
  Lock,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import { PayoutAccount } from "../RequestPayouts";
import { MuiTelInput } from "mui-tel-input";
import { toast } from "react-toastify";

type PaymentMethod = "mobile" | "bank";
type MobileProvider = "ecocash" | "innbucks" | "omari" | "onemoney";

interface PaymentDetailsProps {
  open: boolean;
  onClose: () => void;
  referralId: number | string;
  editingAccount?: PayoutAccount | null;
  onContinue: (account: PayoutAccount) => void;
}

interface PayoutAccountFull {
  id: number;
  method: "ECOCASH" | "INNBUCKS" | "OMARI" | "ONEMONEY" | "BANK";
  ecocash_number?: string;
  bank_name?: string;
  account_name?: string;
  account_number?: string;
  is_default: boolean;
}

const PROVIDER_TO_METHOD: Record<MobileProvider, PayoutAccountFull["method"]> =
  {
    ecocash: "ECOCASH",
    innbucks: "INNBUCKS",
    omari: "OMARI",
    onemoney: "ONEMONEY",
  };

const METHOD_TO_PROVIDER: Partial<
  Record<PayoutAccountFull["method"], MobileProvider>
> = {
  ECOCASH: "ecocash",
  INNBUCKS: "innbucks",
  OMARI: "omari",
  ONEMONEY: "onemoney",
};

const MOBILE_METHODS: PayoutAccountFull["method"][] = [
  "ECOCASH",
  "INNBUCKS",
  "OMARI",
  "ONEMONEY",
];

const providers: { id: MobileProvider; label: string }[] = [
  { id: "ecocash", label: "Ecocash" },
  { id: "innbucks", label: "Innbucks" },
  { id: "omari", label: "Omari" },
  { id: "onemoney", label: "One Money" },
];

const paymentOptions: {
  key: PaymentMethod;
  label: string;
  icon: React.ReactElement;
}[] = [
  {
    key: "mobile",
    label: "Mobile Wallet",
    icon: <PhoneAndroid sx={{ fontSize: 20 }} />,
  },
  {
    key: "bank",
    label: "Bank Transfer",
    icon: <AccountBalance sx={{ fontSize: 20 }} />,
  },
];

export default function PaymentDetails({
  open,
  onClose,

  editingAccount,
  onContinue,
}: PaymentDetailsProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("mobile");
  const [selectedProvider, setSelectedProvider] =
    useState<MobileProvider>("ecocash");

  // Mobile wallet fields
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileAccountName, setMobileAccountName] = useState("");

  // Bank transfer fields
  const [bankName, setBankName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#fafafa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: reduxTheme.primary_colors?.[400] },
      "&.Mui-focused fieldset": {
        borderColor: reduxTheme.primary_colors?.[400],
      },
    },
  };

  useEffect(() => {
    if (!open) return;

    if (editingAccount) {
      // Fetch unmasked account details
      const fetchFullAccount = async () => {
        try {
          const res = await apiService.get<PayoutAccountFull>(
            `/api/wallet/payout-accounts/${editingAccount.id}/`,
          );
          const full = res.data;
          if (MOBILE_METHODS.includes(full.method)) {
            setPaymentMethod("mobile");
            setSelectedProvider(METHOD_TO_PROVIDER[full.method] ?? "ecocash");
            setMobileNumber(full.ecocash_number ?? "");
            setMobileAccountName(full.account_name ?? "");
          } else {
            setPaymentMethod("bank");
            setBankName(full.bank_name ?? "");
            setBankAccountNumber(full.account_number ?? "");
            setBankAccountName(full.account_name ?? "");
          }
        } catch {
          // Fallback to masked data if fetch fails
          if (MOBILE_METHODS.includes(editingAccount.method)) {
            setPaymentMethod("mobile");
            setSelectedProvider(
              METHOD_TO_PROVIDER[editingAccount.method] ?? "ecocash",
            );
            setMobileNumber(editingAccount.masked_ecocash_number ?? "");
            setMobileAccountName(editingAccount.account_name ?? "");
          } else {
            setPaymentMethod("bank");
            setBankName(editingAccount.bank_name ?? "");
            setBankAccountNumber(editingAccount.masked_account_number ?? "");
            setBankAccountName(editingAccount.account_name ?? "");
          }
        }
      };
      fetchFullAccount();
    } else {
      // Adding new — reset to blank
      setPaymentMethod("mobile");
      setSelectedProvider("ecocash");
      setMobileNumber("");
      setMobileAccountName("");
      setBankName("");
      setBankAccountNumber("");
      setBankAccountName("");
    }
  }, [editingAccount, open]);

  const handleContinue = async () => {
    setError(null);

    if (paymentMethod === "mobile") {
      if (!mobileNumber || !mobileAccountName) {
        setError("Please fill in all required fields.");
        return;
      }
    } else {
      if (!bankName || !bankAccountNumber || !bankAccountName) {
        setError("Please fill in all required fields.");
        return;
      }
    }

    try {
      setLoading(true);

      const payload =
        paymentMethod === "mobile"
          ? {
              method: PROVIDER_TO_METHOD[selectedProvider],
              ecocash_number: mobileNumber,
              account_name: mobileAccountName,
              is_default: true,
            }
          : {
              method: "BANK",
              bank_name: bankName,
              account_name: bankAccountName,
              account_number: bankAccountNumber,
              is_default: true,
            };

      const response = editingAccount
        ? await apiService.patch<PayoutAccount>(
            `/api/wallet/payout-accounts/${editingAccount.id}/`,
            payload,
          )
        : await apiService.post<PayoutAccount>(
            "/api/wallet/payout-accounts/",
            payload,
          );
      toast.success(
        editingAccount
          ? "Payment details updated successfully."
          : "Payment details saved successfully.",
      );

      onContinue(response.data);
    } catch (err: unknown) {
      const e = err as Error;
      const message =
        e.message || "Failed to save payment details. Please try again.";
      toast.error(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", m: 2 } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small">
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: reduxTheme.primary_colors?.[400],
                  fontSize: "16px",
                }}
              >
                {editingAccount
                  ? "Edit Payment Details"
                  : "Setup Payment Details"}
              </Typography>
            </Box>
            <IconButton
              size="small"
              sx={{ color: reduxTheme.primary_colors?.[400] }}
              onClick={onClose}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: reduxTheme.primary_colors?.[400],
              ml: 5,
              fontSize: "12px",
            }}
          >
            {editingAccount
              ? "Update where you'd like to receive your earnings."
              : "Add where you'd like to receive your earnings. This is a one-time setup."}
          </Typography>
        </Box>

        {/* Payment Method Selector */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {paymentOptions.map(({ key, label, icon }) => {
              const isSelected = paymentMethod === key;
              return (
                <Box
                  key={key}
                  onClick={() => setPaymentMethod(key)}
                  sx={{
                    flex: 1,
                    p: 2,
                    minHeight: "75px",
                    borderRadius: "14px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                    transition: "all 0.2s ease",
                    border: "2px solid transparent",
                    background: isSelected
                      ? `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`
                      : `linear-gradient(#fff, #fff) padding-box,
                         linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%) border-box`,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isSelected
                        ? reduxTheme.primary_colors?.[900]
                        : "transparent",
                      border: isSelected ? "none" : "1px solid #ccc",
                    }}
                  >
                    {icon.type === PhoneAndroid ? (
                      <PhoneAndroid
                        sx={{
                          color: isSelected
                            ? reduxTheme.primary_colors?.[400]
                            : reduxTheme.primary_colors?.[500],
                          fontSize: 20,
                        }}
                      />
                    ) : (
                      <AccountBalance
                        sx={{
                          color: isSelected
                            ? reduxTheme.primary_colors?.[400]
                            : reduxTheme.primary_colors?.[500],
                          fontSize: 20,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: isSelected
                        ? reduxTheme.primary_colors?.[900]
                        : reduxTheme.primary_colors?.[400],
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: "0.85rem",
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Form Fields */}
        <Box sx={{ px: 3, pb: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {paymentMethod === "mobile" ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#333", fontWeight: 500 }}
                >
                  Mobile Number<span style={{ color: "#c00" }}>*</span>
                </Typography>
                <MuiTelInput
                  fullWidth
                  size="small"
                  value={mobileNumber}
                  onChange={(val) => setMobileNumber(val)}
                  defaultCountry="ZW"
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#333", fontWeight: 500 }}
                >
                  Account Holder Name<span style={{ color: "#c00" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="As it appears on account"
                  size="small"
                  value={mobileAccountName}
                  onChange={(e) => setMobileAccountName(e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {providers.map((provider) => (
                  <Chip
                    key={provider.id}
                    label={provider.label}
                    onClick={() => setSelectedProvider(provider.id)}
                    sx={{
                      borderRadius: 4,
                      background:
                        selectedProvider === provider.id
                          ? `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`
                          : "transparent",
                      color:
                        selectedProvider === provider.id
                          ? reduxTheme.primary_colors?.[900]
                          : "#666",
                      border:
                        selectedProvider === provider.id
                          ? "none"
                          : "1px solid #e0e0e0",
                      fontWeight: selectedProvider === provider.id ? 600 : 400,
                    }}
                  />
                ))}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#333", fontWeight: 500 }}
                >
                  Bank Name<span style={{ color: "#c00" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g. CABS"
                  size="small"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#333", fontWeight: 500 }}
                >
                  Account Number<span style={{ color: "#c00" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter account number"
                  size="small"
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  sx={textFieldSx}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#333", fontWeight: 500 }}
                >
                  Account Holder Name<span style={{ color: "#c00" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="As it appears on account"
                  size="small"
                  value={bankAccountName}
                  onChange={(e) => setBankAccountName(e.target.value)}
                  sx={textFieldSx}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Continue Button */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleContinue}
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: 3,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`,
              boxShadow: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Continue"
            )}
          </Button>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            pb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Lock sx={{ fontSize: 14, color: "#999" }} />
          <Typography variant="caption" sx={{ color: "#999" }}>
            Securely stored. Used only for your payouts.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
